import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatusBadge } from "@/components/Badges";
import { Receipt, Check, Loader2, X, ChevronDown, ChevronRight } from "lucide-react";

export default function InvoiceTracking() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null); // task being marked

  const load = () => {
    setLoading(true);
    api.get("/invoices/by-project").then(r => setProjects(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const totals = projects.reduce((acc, p) => ({
    billable: acc.billable + p.billable_tasks,
    invoiced: acc.invoiced + p.invoiced_count,
    pending: acc.pending + p.pending_count,
    amount: acc.amount + (p.invoiced_amount || 0),
  }), { billable: 0, invoiced: 0, pending: 0, amount: 0 });

  return (
    <div className="space-y-6 animate-fade-in">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Finance</div>
        <h1 className="text-3xl font-semibold tracking-tight text-gray-900 mt-2">Invoice Tracking</h1>
        <p className="text-sm text-gray-500 mt-1.5">Per-project view of which tasks have been invoiced.</p>
      </header>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Billable tasks" value={totals.billable} />
        <Stat label="Invoiced" value={totals.invoiced} tone="green" />
        <Stat label="Pending" value={totals.pending} tone={totals.pending > 0 ? "amber" : "neutral"} />
        <Stat label="Invoiced ₹" value={`₹${totals.amount.toLocaleString("en-IN")}`} />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : projects.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-200 rounded-lg p-10 text-center text-sm text-gray-400">No projects yet.</div>
      ) : (
        <div className="space-y-3">
          {projects.map(p => {
            const open = !!expanded[p.id];
            const billable = p.tasks.filter(t => t.invoice_required);
            const progress = p.billable_tasks === 0 ? 0 : Math.round((p.invoiced_count / p.billable_tasks) * 100);
            return (
              <div key={p.id} data-testid={`invoice-project-${p.id}`} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpanded(s => ({ ...s, [p.id]: !s[p.id] }))}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 text-left"
                >
                  {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3">
                      <div className="text-sm font-semibold text-gray-900 tracking-tight">{p.name}</div>
                      <div className="text-[11px] uppercase tracking-wider text-gray-500">{p.client_name}</div>
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="w-44 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${progress}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 font-mono">
                        {p.invoiced_count}/{p.billable_tasks} invoiced
                        {p.pending_count > 0 && <span className="text-amber-700 ml-2">· {p.pending_count} pending</span>}
                      </span>
                    </div>
                  </div>
                  {p.invoiced_amount > 0 && (
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-wider text-gray-500">Invoiced</div>
                      <div className="text-sm font-mono font-semibold text-gray-900">₹{Number(p.invoiced_amount).toLocaleString("en-IN")}</div>
                    </div>
                  )}
                </button>

                {open && (
                  <div className="border-t border-gray-100">
                    {billable.length === 0 ? (
                      <div className="px-5 py-6 text-sm text-gray-400 italic">No billable tasks in this project.</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50/60 text-[11px] uppercase tracking-wider text-gray-500">
                          <tr>
                            <th className="text-left px-5 py-2.5 font-medium">Task</th>
                            <th className="text-left px-3 py-2.5 font-medium">Status</th>
                            <th className="text-left px-3 py-2.5 font-medium">Invoice #</th>
                            <th className="text-left px-3 py-2.5 font-medium">Date</th>
                            <th className="text-right px-3 py-2.5 font-medium">Amount</th>
                            <th className="text-right px-5 py-2.5 font-medium">Invoiced?</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {billable.map(t => (
                            <tr key={t.id} data-testid={`invoice-row-${t.id}`} className="hover:bg-gray-50">
                              <td className="px-5 py-3">
                                <div className="font-medium text-gray-900">{t.title}</div>
                                <div className="text-xs text-gray-500 mt-0.5">{t.assignee_name} · Due {t.due_date || "—"}</div>
                              </td>
                              <td className="px-3 py-3"><StatusBadge status={t.status} /></td>
                              <td className="px-3 py-3 text-gray-700 font-mono text-xs">{t.invoice_number || <span className="text-gray-300">—</span>}</td>
                              <td className="px-3 py-3 text-gray-700 font-mono text-xs">{t.invoice_date || <span className="text-gray-300">—</span>}</td>
                              <td className="px-3 py-3 text-right text-gray-900 font-mono">{t.invoice_amount ? `₹${Number(t.invoice_amount).toLocaleString("en-IN")}` : <span className="text-gray-300">—</span>}</td>
                              <td className="px-5 py-3 text-right">
                                <button
                                  onClick={() => setEditing(t)}
                                  data-testid={`invoice-mark-${t.id}`}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                    t.invoiced
                                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                      : "border-amber-200 bg-amber-50 text-amber-700 hover:border-navy hover:text-navy"
                                  }`}
                                >
                                  {t.invoiced ? <><Check className="w-3 h-3" /> Invoiced</> : <><Receipt className="w-3 h-3" /> Pending</>}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && <InvoiceEditModal task={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function Stat({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: "border-gray-200",
    green:   "border-emerald-200 bg-emerald-50/30",
    amber:   "border-amber-200 bg-amber-50/30",
  };
  return (
    <div className={`bg-white border ${tones[tone]} rounded-lg p-4`}>
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-xl font-mono font-semibold text-gray-900 mt-1.5">{value}</div>
    </div>
  );
}

function InvoiceEditModal({ task, onClose, onSaved }) {
  const [form, setForm] = useState({
    invoiced: !!task.invoiced,
    invoice_number: task.invoice_number || "",
    invoice_date: task.invoice_date || "",
    invoice_amount: task.invoice_amount || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = {
        invoiced: form.invoiced,
        invoice_number: form.invoice_number || null,
        invoice_date: form.invoice_date || null,
        invoice_amount: form.invoice_amount === "" ? null : Number(form.invoice_amount),
      };
      await api.patch(`/tasks/${task.id}`, payload);
      onSaved && onSaved(); onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} data-testid="invoice-edit-modal" className="bg-white max-w-md w-full rounded-xl shadow-xl border border-gray-200 p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Invoice details</h2>
            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{task.title}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
        </div>

        <label className="flex items-center gap-2.5 px-3 py-2.5 border border-gray-200 rounded-md bg-gray-50 cursor-pointer">
          <input type="checkbox" checked={form.invoiced} onChange={(e) => setForm(f => ({ ...f, invoiced: e.target.checked }))} data-testid="invoice-mark-checkbox" className="w-4 h-4 accent-[#2B4462]" />
          <span className="text-sm font-medium text-gray-900">Mark as invoiced</span>
        </label>

        <Field label="Invoice number"><input value={form.invoice_number} onChange={(e) => setForm(f => ({ ...f, invoice_number: e.target.value }))} data-testid="invoice-number-input" className="input" placeholder="INV-2026-001" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Invoice date"><input type="date" value={form.invoice_date} onChange={(e) => setForm(f => ({ ...f, invoice_date: e.target.value }))} data-testid="invoice-date-input" className="input" /></Field>
          <Field label="Amount (₹)"><input type="number" step="0.01" value={form.invoice_amount} onChange={(e) => setForm(f => ({ ...f, invoice_amount: e.target.value }))} data-testid="invoice-amount-input" className="input" placeholder="0" /></Field>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded">Cancel</button>
          <button type="submit" disabled={busy} data-testid="invoice-save-btn" className="bg-navy hover:bg-navy-800 text-white px-4 py-2 rounded text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}Save
          </button>
        </div>
        <style>{`.input{width:100%;background:white;border:1px solid #d1d5db;border-radius:6px;padding:8px 10px;font-size:14px;color:#111827;outline:none}.input:focus{border-color:#2B4462;box-shadow:0 0 0 3px rgba(43,68,98,.12)}`}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
